import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';
import { Tip } from './Tooltip';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-quiet';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const base =
  'relative inline-flex shrink-0 cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium ' +
  'transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out active:translate-y-px ' +
  'disabled:pointer-events-none disabled:opacity-50 aria-busy:pointer-events-none';

export const buttonVariants: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-ink shadow-[inset_0_1px_0_rgb(255_255_255/0.16),0_1px_2px_rgb(0_0_0/0.14)] hover:bg-accent-hover active:bg-accent-press',
  secondary: 'border border-line-strong bg-surface text-ink shadow-xs hover:border-field-line hover:bg-hover',
  ghost: 'text-ink-2 hover:bg-hover hover:text-ink',
  danger: 'bg-danger text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(0_0_0/0.14)] hover:bg-danger-hover',
  'danger-quiet': 'text-danger-text hover:bg-danger-soft',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-label',
  md: 'h-10 px-4 text-compact',
  lg: 'h-12 px-5 text-body',
  xl: 'h-14 px-6 text-[1rem]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, icon, iconRight, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      aria-busy={loading || undefined}
      className={cn(base, buttonVariants[variant], sizes[size], block && 'w-full', className)}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  );
});

type IconButtonSize = 'sm' | 'md' | 'lg';
const iconSizes: Record<IconButtonSize, string> = { sm: 'size-8', md: 'size-10', lg: 'size-11' };

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Accessible name, also shown as a tooltip. */
  label: string;
  variant?: ButtonVariant;
  size?: IconButtonSize;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  hideTooltip?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', size = 'md', className, children, tooltipSide = 'top', hideTooltip, type = 'button', ...rest },
  ref,
) {
  const button = (
    <button ref={ref} type={type} aria-label={label} className={cn(base, buttonVariants[variant], iconSizes[size], 'px-0', className)} {...rest}>
      {children}
    </button>
  );
  return hideTooltip ? button : <Tip label={label} side={tooltipSide}>{button}</Tip>;
});
